import {
  JupiterTheme,
  propertyRevisionData,
} from '@jupiterone/jupiter-web-toolkit';
import { Tab, Tabs } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { InspectEdge } from 'jupiter-graph-viewer/src/components/types';
import EntityDrawerContent from 'jupiter-graph-viewer/src/components/GraphViewerEntityDrawer/EntityDrawerContent';
import EntityDrawerHeader from 'jupiter-graph-viewer/src/components/GraphViewerEntityDrawer/EntityDrawerHeader';
import EntityDrawerProperty from 'jupiter-graph-viewer/src/components/GraphViewerEntityDrawer/EntityDrawerProperty';
import {
  getActiveProperties,
  initialState,
  readAndSortProperties,
  RelationshipDrawerState,
  selectTab,
  TabIdentifier,
} from './RelationshipDrawer.reducers';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    drawerTitleBar: {
      display: 'flex',
      flex: '1 0 auto',
      justifyContent: 'space-between',
      '& $titleText': {
        marginTop: '.75rem',
      },
    },
    relationLabel: {
      color: theme.palette.blue[100],
    },
    titleText: {
      color: theme.palette.navy[500],
    },
    header: {
      width: '100%',
      backgroundColor: theme.palette.primary.main,
      borderTop: `solid 4px ${theme.palette.primary.dark}`,
      padding: 0,
    },
    headerTitle: {
      display: 'flex',
      padding: theme.spacing(1, 2, 0, 3),
    },
    headerTitleText: {
      color: theme.palette.common.white,
      flex: 1,
      lineHeight: 1.25,
      alignSelf: 'center',
      textTransform: 'none',
      maxWidth: 400,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingTop: theme.spacing(0.75),
    },
    headerSubText: {
      display: 'block',
      clear: 'left',
      fontWeight: 'normal',
      lineHeight: 1.3,
      marginTop: theme.spacing(0.375),
      marginBottom: theme.spacing(0.75),
      color: theme.palette.blue[100],
    },
    headerActions: {
      position: 'relative',
    },
    tabLabel: {
      color: theme.palette.common.white,
      fontSize: theme.typography.body1.fontSize,
      display: 'inline-block',
      margin: theme.spacing(0, 1),
    },
    headerTitleClose: {
      color: theme.palette.blue[100],
      width: 24,
      height: 24,
      fontSize: 24,
      margin: 6,
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.common.white,
      },
    },
  })
);

interface Props {
  edge: InspectEdge;
  onClose: () => void;
}
const RelationshipDrawer = ({ edge, onClose }: Props) => {
  const classes = useStyles();
  const [
    relationshipDrawerState,
    setRelationshipDrawerState,
  ] = useState<RelationshipDrawerState>(initialState);
  const { _class, _type, displayName } = edge.relationship;
  const relationshipLabel =
    displayName && displayName !== _class && displayName !== _type
      ? displayName
      : ' → ';

  useEffect(() => {
    setRelationshipDrawerState(readAndSortProperties(edge));
  }, [edge]);

  const handleTabChange = useCallback(
    (_event: any, tabValue: TabIdentifier) => {
      setRelationshipDrawerState(selectTab(tabValue));
    },
    []
  );
  const title = (
    <>
      <small>{edge.fromEntity.displayName}</small>
      &nbsp;
      <small className={classes.relationLabel}>{relationshipLabel}</small>
      &nbsp;
      <small>{edge.toEntity.displayName}</small>
    </>
  );
  const subtitle = `${edge.relationship._type} (${edge.relationship._class})`;

  return (
    <>
      <EntityDrawerHeader title={title} subtitle={subtitle} onClose={onClose}>
        <Tabs
          centered
          value={relationshipDrawerState.selectedTab}
          onChange={handleTabChange}
        >
          {relationshipDrawerState.tabs.map(({ identifier, label }) => (
            <Tab
              className={classes.tabLabel}
              key={identifier}
              value={identifier}
              label={label}
            />
          ))}
        </Tabs>
      </EntityDrawerHeader>
      <EntityDrawerContent>
        {getActiveProperties(relationshipDrawerState).map(
          (propertyName: string) => (
            <EntityDrawerProperty
              propertyName={propertyName}
              propertyData={propertyRevisionData(propertyName, {
                properties: edge.properties,
              })}
              key={`${edge.id}:${propertyName}`}
              entityType={'relationship'}
            />
          )
        )}
      </EntityDrawerContent>
    </>
  );
};

export default RelationshipDrawer;
